/**
 * « Relevé de transport » d'un brouillon : ce qui sera facturé à la Coop, voyage par voyage.
 * Les montants sont ceux du brouillon, arrondis ligne par ligne par le serveur ; rien n'est recalculé ici.
 * Le nom d'un autre client ne sort jamais de ce modèle : un départ après un autre client ne garde que
 * son type (« après une livraison »).
 */
import { billableCalculationTotals, billableHoursTotal, billingNumber } from "./billingCalculation.js";
import { billableSingleHourlyRate, groupBillableCalculationDays, pmLoadingWithRoad, summarizeBillableTime } from "./billingCalculationDays.js";
import { billingCustomerText } from "./billingPrestationDetails.js";

const LOADS = ["loading", "preload"];
const DROPS = ["delivery", "dompeur"];
const EXTRA_LABELS = { tonne_supplement: "Supplément à la tonne", voyage_supplement: "Supplément voyage" };
// Au-delà de cet écart, une heure facturée n'est pas « confirmée » par le GPS.
export const GPS_CONFIRMED_MINUTES = 2;

const sum = (values) => values.reduce((total, value) => total + value, 0);
// Un total n'est connu que si chaque partie l'est.
const knownSum = (values) => values.some((value) => value == null) ? null : sum(values);
const unique = (values) => [...new Set(values.filter(Boolean))];
const instant = (value) => Number.isFinite(Date.parse(value)) ? Date.parse(value) : null;
const amountOf = (items) => items.length ? billableCalculationTotals(items)[0].amount : 0;
const embeddedImage = (value) => /^data:image\/(png|jpeg|webp);base64,/.test(value || "") ? value : null;

/** Lignes du brouillon lues comme des prestations : chargement, inspection et retour portent un camion, pas une commande. */
function statementItems(invoice) {
  const currency = /^[A-Z]{3}$/.test(invoice?.currency_code || "") ? invoice.currency_code : "CAD";
  return (Array.isArray(invoice?.lines) ? invoice.lines : []).filter((line) => line && typeof line === "object").map((line) => ({
    ...line, currency_code: currency,
    order_reference: line.presentation_context?.order_id || !line.calculation_details ? billingCustomerText(line.order_reference) : null,
  }));
}

// Temps retiré d'un bloc (dîner, pause, préchargement) : où il tombe, lu sur le bloc enregistré lui-même.
function removedTime(step) {
  const block = step.block || {};
  const [start, arrival, end] = [block.start, block.arrival, block.end].map(instant);
  const net = billingNumber(block.net_seconds);
  // Seule la part de ce bloc facturée à ce client compte, comme pour les kilomètres.
  const share = net && step.seconds !== null ? Math.min(step.seconds / net, 1) : 1;
  const gap = (from, to, kept) => from === null || to === null || kept === null ? 0 : Math.max(0, (to - from) / 1000 - kept) * share;
  return { road: gap(start, arrival, billingNumber(block.travel_seconds)), site: gap(arrival, end, billingNumber(block.onsite_seconds)) };
}

function stopOf(step) {
  const removed = removedTime(step);
  const orders = unique(step.items.map((item) => item.order_reference));
  // Chargé PM : sa route vers la meunerie est comprise dans son chargement, la route du voyage reste connue.
  const included = pmLoadingWithRoad(step);
  return {
    kind: step.kind, site: step.site, tons: step.tons, km: step.km,
    start: step.block?.start || null, arrival: step.block?.arrival || null, departure: step.block?.end || null,
    // Sans partage enregistré ni estimé, tout le temps facturé est compté sur place.
    travel: included ? 0 : step.travel, onsite: step.travel === null ? step.seconds : step.onsite, included, estimated: step.estimated,
    removedRoad: removed.road, removedSite: removed.site, orders,
  };
}

function extraLabel(step) {
  if (step.kind === "fixed_fee") return step.items[0]?.calculation_details?.fixed_fee_source === "aspiration" ? "Aspiration" : "Montant fixe";
  return EXTRA_LABELS[step.kind] || "Autre frais";
}

function voyageOf(day, parts, index) {
  const loads = parts.loads.map(stopOf);
  const drops = parts.drops.map(stopOf);
  const stops = [...loads, ...drops];
  const first = parts.loads[0] || parts.drops[0];
  const items = [...parts.loads, ...parts.drops, ...parts.shared, ...parts.extras].flatMap((step) => step.items);
  const pm = parts.loads.flatMap((step) => step.items).find((item) => item.calculation_details?.pm_delivery_period);
  // La part du départ (inspection) et du retour au garage compte dans la route : avant le premier trajet, après le dernier.
  const [departure, back] = ["inspection", "return"].map((kind) => knownSum(parts.shared.filter((step) => step.kind === kind).map((step) => step.seconds)));
  const road = (legs, first, last) => knownSum([...legs.map((stop) => stop.travel), first ? departure : 0, last ? back : 0]);
  return {
    // Le relevé nomme le camion, jamais le chauffeur.
    key: `${day.key}:${index}`, date: day.date, period: day.period || "day", truck: day.truck,
    timezone: day.timezone || "America/Toronto",
    // « day_start » : début de journée ; « visit » : après un arrêt d'un autre client, jamais nommé.
    origin: first.context.origin_kind, departure: stops[0].start, loads, drops,
    estimatedLoads: loads.some((stop) => stop.estimated), estimatedDrops: drops.some((stop) => stop.estimated),
    // La distance d'une route comprise dans le chargement est donnée avec lui.
    toMill: { seconds: road(loads, loads.length > 0, !drops.length),
      km: loads.some((stop) => !stop.included) ? knownSum(loads.filter((stop) => !stop.included).map((stop) => stop.km)) : null },
    loading: { seconds: knownSum(loads.map((stop) => stop.onsite)), tons: knownSum(loads.map((stop) => stop.tons)) },
    toClient: { seconds: road(drops, !loads.length, drops.length > 0), km: knownSum(drops.map((stop) => stop.km)) },
    onsite: knownSum(drops.map((stop) => stop.onsite)),
    tons: knownSum(drops.map((stop) => stop.tons)),
    // Un Chargé PM est livré par un autre voyage (le soir même ou un autre jour) : ce voyage garde les commandes qu'il charge.
    orders: unique((drops.length ? drops : loads).flatMap((stop) => stop.orders)),
    pm: pm ? { date: pm.service_date, period: pm.calculation_details.pm_delivery_period } : null,
    // Version en ligne : chaque commande livrée, avec sa ferme et ses tonnes (une commande seule sur l'arrêt prend celles de l'arrêt).
    deliveries: parts.drops.flatMap((step, index) => drops[index].orders.map((reference) => ({
      reference, farm: drops[index].site,
      tons: billingNumber(step.items.find((item) => item.order_reference === reference)?.presentation_context?.tonnage)
        ?? (drops[index].orders.length === 1 ? drops[index].tons : null),
    }))),
    removed: sum(stops.map((stop) => stop.removedRoad + stop.removedSite)),
    extras: parts.extras.map((step) => ({ label: extraLabel(step), amount: amountOf(step.items) })),
    // Barre de couleur : dans l'ordre du voyage, le départ, la route avant chaque arrêt puis le temps sur place, et le retour.
    segments: [
      { kind: "road", seconds: departure || 0 },
      ...stops.flatMap((stop) => [{ kind: "road", seconds: stop.travel || 0 }, { kind: DROPS.includes(stop.kind) ? "delivery" : "loading", seconds: stop.onsite || 0 }]),
      { kind: "road", seconds: back || 0 },
    ].filter((segment) => segment.seconds > 0),
    hours: billableHoursTotal(items), amount: amountOf(items), items,
  };
}

/**
 * Une journée de chauffeur coupée en voyages : chaque chargement après une livraison ouvre un voyage.
 * Le Chargé PM ferme la journée du camion qui le charge : un voyage à lui, avec sa part de l'inspection et du retour.
 */
function dayVoyages(day) {
  const groups = [];
  const shared = [];
  const extras = [];
  const pm = { loads: [], drops: [], shared: [], extras: [] };
  let current = null;
  const open = () => groups.push(current = { loads: [], drops: [], shared: [], extras: [] });
  for (const step of day.steps) {
    if (step.items.some((item) => item.calculation_details?.pm_delivery_period)) (LOADS.includes(step.kind) ? pm.loads : pm.shared).push(step);
    else if (LOADS.includes(step.kind)) {
      if (!current || current.drops.length) open();
      current.loads.push(step);
    } else if (DROPS.includes(step.kind)) {
      if (!current) open();
      current.drops.push(step);
    } else if (step.kind === "inspection" || step.kind === "return") shared.push(step);
    else extras.push(step);
  }
  if (!pm.loads.length) shared.push(...pm.shared);
  // Le reste du temps commun et les suppléments vont aux voyages de livraison ; au Chargé PM s'il est seul.
  const owners = groups.length ? [...groups] : pm.loads.length ? [pm] : [];
  if (pm.loads.length) groups.push(pm);
  if (!groups.length) return { voyages: [], rest: day.items };
  // L'inspection ouvre la journée et le retour la ferme.
  for (const step of shared) (step.kind === "inspection" ? owners[0] : owners.at(-1)).shared.push(step);
  for (const step of extras) {
    const references = step.items.map((item) => item.order_reference).filter(Boolean);
    const owner = owners.find((group) => group.drops.some((drop) => drop.items.some((item) => references.includes(item.order_reference))));
    (owner || owners.at(-1)).extras.push(step);
  }
  return { voyages: groups.map((group, index) => voyageOf(day, group, index)), rest: [] };
}

/** Le relevé d'un brouillon Consolidation ; les lignes sans relevé de journée restent dans « Autres frais ». */
export function buildTransportStatement(invoice, { preparedOn = new Date() } = {}) {
  const items = statementItems(invoice);
  const sheetItems = items.filter((item) => item.calculation_details?.revision_id && !item.is_manual);
  const voyages = [];
  const voyageItems = [];
  const preloadItems = [];
  const preloadDates = [];
  const preloadTrucks = [];
  let preloadCount = 0;
  const others = items.filter((item) => !sheetItems.includes(item));
  for (const day of groupBillableCalculationDays(sheetItems)) {
    const preloadSteps = day.steps.filter((step) => step.kind === "preload");
    const dayPreloads = preloadSteps.flatMap((step) => step.items);
    preloadCount += preloadSteps.length;
    preloadItems.push(...dayPreloads);
    if (dayPreloads.length) { preloadDates.push(day.date); preloadTrucks.push(day.truck); }
    const { voyages: dayTrips, rest } = dayVoyages({ ...day,
      steps: day.steps.filter((step) => step.kind !== "preload"),
      items: day.items.filter((item) => !dayPreloads.includes(item)),
    });
    // Les lignes brutes gardent les lieux des autres clients : elles ne sortent pas du modèle.
    for (const { items: tripItems, ...voyage } of dayTrips) {
      voyages.push(voyage);
      voyageItems.push(...tripItems);
    }
    others.push(...rest);
  }
  const serviceItems = [...voyageItems, ...preloadItems];
  const hourly = serviceItems.filter((item) => item.billing_unit === "heure");
  const time = summarizeBillableTime(serviceItems);
  const subtotal = billingNumber(invoice?.subtotal);
  const surcharge = billingNumber(invoice?.fuel_surcharge_amount) || 0;
  const drops = voyages.flatMap((voyage) => voyage.drops);
  const legs = voyages.flatMap((voyage) => [...voyage.loads, ...voyage.drops]);
  const dates = [...voyages.map((voyage) => voyage.date), ...preloadDates].filter(Boolean).sort();
  return {
    // Numéro RT attribué à l'envoi ; un brouillon n'en a pas encore.
    number: billingCustomerText(invoice?.invoice_number),
    clientName: billingCustomerText(invoice?.billing_account_name) || "Client",
    clientAddress: billingCustomerText(invoice?.billing_address),
    issuer: {
      name: billingCustomerText(invoice?.issuer?.legal_name), email: billingCustomerText(invoice?.issuer?.email),
      phone: billingCustomerText(invoice?.issuer?.phone), address: billingCustomerText(invoice?.issuer?.address),
    },
    // Logos de l'émetteur et de la Coop quand ils existent : seule une image intégrée est acceptée.
    issuerLogo: embeddedImage(invoice?.issuer?.logo_data_url),
    clientLogo: embeddedImage(invoice?.billing_account?.logo_data_url),
    currency: items[0]?.currency_code || "CAD",
    periodStart: invoice?.period_start || dates[0] || "", periodEnd: invoice?.period_end || dates.at(-1) || "",
    preparedOn,
    amounts: {
      subtotal, surcharge, surchargePercent: billingNumber(invoice?.fuel_surcharge_percent) || 0,
      beforeTaxes: subtotal === null ? null : Math.round((subtotal + surcharge) * 100) / 100,
      transport: amountOf(hourly), extras: subtotal === null ? null : Math.round((subtotal - amountOf(hourly)) * 100) / 100,
      taxes: (billingNumber(invoice?.gst_rate_percent) || 0) + (billingNumber(invoice?.qst_rate_percent) || 0) > 0,
      // TPS et TVQ du relevé (activées dans les Paramètres) : le total est alors taxes comprises, comme la facture.
      gst: billingNumber(invoice?.gst_amount) || 0, gstPercent: billingNumber(invoice?.gst_rate_percent) || 0,
      qst: billingNumber(invoice?.qst_amount) || 0, qstPercent: billingNumber(invoice?.qst_rate_percent) || 0,
      total: billingNumber(invoice?.total),
    },
    hours: billableHoursTotal(hourly), rate: billableSingleHourlyRate(hourly),
    time: {
      loading: { seconds: time.loading.seconds, count: time.loading.count, tons: time.loading.tons, estimated: time.loading.estimated },
      // La route compte aussi la part du départ (inspection) et du retour au garage ; un dompeur n'a pas de retour facturé.
      route: { seconds: time.route.seconds + time.inspection + time.back, count: time.route.count, km: time.route.km,
        departure: time.inspection, back: time.back, estimated: time.route.estimated },
      delivery: { seconds: time.delivery.seconds, count: time.delivery.count, estimated: time.delivery.estimated },
      other: time.other,
    },
    counts: {
      days: unique(dates).length, voyages: voyages.length, deliveries: drops.length,
      orders: unique(drops.flatMap((stop) => stop.orders)).length,
      tons: knownSum(drops.map((stop) => stop.tons)), farms: unique(drops.map((stop) => stop.site)).length,
      km: knownSum(legs.map((stop) => stop.km)), trucks: unique([...voyages.map((voyage) => voyage.truck), ...preloadTrucks]).length,
    },
    voyages,
    preloads: { count: preloadCount, hours: billableHoursTotal(preloadItems), amount: amountOf(preloadItems) },
    others: others.map((item) => ({ label: billingCustomerText(item.description) || "Autre frais", amount: amountOf([item]) })),
  };
}

/** Le brouillon a-t-il au moins un relevé de journée à présenter ? */
/** Même règle que le serveur (is_transport_statement) : un brouillon de Consolidation s'envoie en relevé RT. */
export const isTransportStatement = (invoice) => invoice?.billing_origin === "consolidation" && (invoice?.document_kind || "invoice") === "invoice";

export function hasTransportStatement(invoice) {
  return Array.isArray(invoice?.lines) && invoice.lines.some((line) => line?.calculation_details?.revision_id && !line.is_manual);
}

function minuteOfDay(value, timeZone) {
  const time = instant(value);
  if (time === null) return null;
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, hourCycle: "h23", hour: "2-digit", minute: "2-digit", second: "2-digit" })
    .formatToParts(new Date(time));
  const part = (type) => Number(parts.find((entry) => entry.type === type)?.value || 0);
  return part("hour") * 60 + part("minute") + part("second") / 60;
}

const gpsMinute = (value) => Number.isFinite(value) ? value : null;

/** Comparaison Geotab d'un voyage : le Soir a la sienne ; le Jour garde la date seule, clé des relevés déjà figés. */
export const voyageGpsKey = (voyage) => voyage.period === "evening" ? `${voyage.date}@evening` : voyage.date;

// Écart lu sur les minutes affichées : deux heures identiques à l'écran ne montrent jamais d'écart.
function gap(billed, gps) {
  return billed === null || gps === null ? null : Math.abs(Math.round(billed) - Math.round(gps));
}

// Un arrêt retrouve son site par ses commandes, car le chauffeur a pu changer l'ordre prévu.
// Un chargement n'a pas de commande : son nom, sinon le site resté libre.
function pairSites(stops, sites) {
  const free = [...sites];
  const take = (wanted) => {
    const site = free.find(wanted);
    if (site) free.splice(free.indexOf(site), 1);
    return site || null;
  };
  const found = stops.map((stop) => take(stop.orders.length
    ? (site) => site.order_ids.some((reference) => stop.orders.includes(reference))
    : (site) => site.name === stop.site));
  return stops.map((stop, index) => ({ stop, site: found[index] || (stop.orders.length ? null : take(() => true)) }));
}

/**
 * Preuve GPS d'un voyage : arrivée et départ facturés face aux arrêts Geotab du même camion
 * (comparaison de Consolidation, plan de clôture). Les arrêts en route ne sont pas montrés :
 * la route est facturée du départ à l'arrivée. L'arrêt précédent d'un autre client reste anonyme.
 */
export function buildVoyageGpsProof(voyage, comparison, ownOrders = []) {
  const references = new Set(voyage.orders);
  const own = new Set(ownOrders);
  const matches = (site) => (site?.order_ids || []).some((reference) => references.has(reference));
  const trucks = (comparison?.trucks || []).filter((truck) => Array.isArray(truck?.comparison?.sites));
  // Un Chargé PM, livré par un autre voyage, se retrouve par son chargement.
  const kind = voyage.drops.length ? "delivery" : "loading";
  const truck = trucks.find((candidate) => candidate.comparison.sites.some((site) => site?.kind === kind && matches(site)));
  if (!truck) return null;
  const sites = truck.comparison.sites.filter(Boolean);
  const pick = (kind) => sites.filter((site) => site.kind === kind && matches(site)).sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0));
  const zone = voyage.timezone;
  const pairs = [...pairSites(voyage.loads, pick("loading")), ...pairSites(voyage.drops, pick("delivery"))];
  const firstRank = Math.min(...pairs.map(({ site }) => site?.real_rank).filter(Number.isFinite));
  const previous = Number.isFinite(firstRank) && firstRank > 1 ? sites.find((site) => site.real_rank === firstRank - 1) : null;
  const dayStart = voyage.origin === "day_start" || firstRank === 1;
  const previousIsOwn = previous && (previous.order_ids || []).some((reference) => own.has(reference));
  const start = {
    key: "start", kind: "start",
    label: dayStart ? "Début de journée" : previousIsOwn ? previous.name : previous?.kind === "loading" ? "Chargement précédent" : "Livraison précédente",
    masked: !dayStart && !previousIsOwn,
    billedDeparture: minuteOfDay(voyage.departure, zone),
    gpsDeparture: gpsMinute(dayStart ? truck.comparison.real_departure : previous?.real_departure),
  };
  const rows = [start, ...pairs.map(({ stop, site }, index) => ({
    key: `stop-${index}`, kind: DROPS.includes(stop.kind) ? "delivery" : "loading", label: stop.site,
    billedArrival: minuteOfDay(stop.arrival, zone), billedDeparture: minuteOfDay(stop.departure, zone),
    gpsArrival: gpsMinute(site?.real_arrival), gpsDeparture: gpsMinute(site?.real_departure),
    distance: Number.isFinite(site?.offset_m) ? site.offset_m : null, seen: Boolean(site && Number.isFinite(site.real_arrival)),
  }))].map((row) => {
    const gaps = [gap(row.billedArrival ?? null, row.gpsArrival ?? null), gap(row.billedDeparture ?? null, row.gpsDeparture ?? null)];
    const known = gaps.filter((value) => value !== null);
    const compared = [row.billedArrival, row.billedDeparture].filter((value) => value != null).length;
    return { ...row, gap: known.length ? Math.max(...known) : null, compared, confirmed: gaps.filter((value) => value !== null && value <= GPS_CONFIRMED_MINUTES).length };
  });
  return { truck: truck.truck_name || voyage.truck, rows, removedLabel: removedLabel(pairs, truck, zone),
    compared: sum(rows.map((row) => row.compared)), confirmed: sum(rows.map((row) => row.confirmed)) };
}

// « dîner » quand le dîner enregistré en Consolidation tombe sur le temps retiré du voyage, sinon « retiré ».
function removedLabel(pairs, truck, zone) {
  const lunch = Array.isArray(truck.consolidation_lunch) && truck.consolidation_lunch.every(Number.isFinite) ? truck.consolidation_lunch : null;
  const overlaps = (from, to) => lunch && from !== null && to !== null && Math.min(to, lunch[1]) > Math.max(from, lunch[0]);
  return pairs.some(({ stop }) => {
    const [start, arrival, end] = [stop.start, stop.arrival, stop.departure].map((value) => minuteOfDay(value, zone));
    return (stop.removedRoad >= 30 && overlaps(start, arrival)) || (stop.removedSite >= 30 && overlaps(arrival, end));
  }) ? "dîner" : "retiré";
}

export function distanceLabel(meters) {
  return meters >= 1000 ? `${new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 1 }).format(meters / 1000)} km` : `${Math.round(meters)} m`;
}
